import React, {useMemo, useState} from "react";

const defaultConfig = {
    app_name: "Tatwerat POS",
    login_label: "Username",
    login_name_placeholder: "Enter username or email",
};

const EyeIcon = ({hidden}) => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        {hidden ? (
            <>
                <path d="M3 3l18 18"/>
                <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8"/>
                <path d="M9.9 5.2A10.7 10.7 0 0 1 12 5c5 0 8.6 4.3 10 7a14.6 14.6 0 0 1-3 4"/>
                <path d="M6.6 6.6A14.2 14.2 0 0 0 2 12c1.4 2.7 5 7 10 7 1.6 0 3-.4 4.3-1"/>
            </>
        ) : (
            <>
                <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/>
                <circle cx="12" cy="12" r="3"/>
            </>
        )}
    </svg>
);

const UserIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20 21a8 8 0 0 0-16 0"/>
        <circle cx="12" cy="7" r="4"/>
    </svg>
);

const LockIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="4" y="10" width="16" height="10" rx="2"/>
        <path d="M8 10V7a4 4 0 0 1 8 0v3"/>
    </svg>
);

const MailIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="2"/>
        <path d="m3 7 9 6 9-6"/>
    </svg>
);

const CheckIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20 6 9 17l-5-5"/>
    </svg>
);

const ArrowIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 12h14"/>
        <path d="m13 6 6 6-6 6"/>
    </svg>
);

const AlertIcon = ({success}) => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        {success ? (
            <>
                <circle cx="12" cy="12" r="10"/>
                <path d="m8 12 3 3 5-6"/>
            </>
        ) : (
            <>
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 7v6"/>
                <path d="M12 17h.01"/>
            </>
        )}
    </svg>
);

const getWindowConfig = () => window.trx_pos || {};

const getRememberedUsername = () => {
    try {
        return window.localStorage?.getItem("trx_pos_login_username") || "";
    } catch {
        return "";
    }
};

const setRememberedUsername = (username, remember) => {
    try {
        if (remember) {
            window.localStorage?.setItem("trx_pos_login_username", username);
        } else {
            window.localStorage?.removeItem("trx_pos_login_username");
        }
    } catch {
        // Remember me is only a convenience; login should continue if storage is unavailable.
    }
};

const getErrorMessage = (error, fallback) => (
    error?.responseJSON?.message
    || error?.responseJSON?.exception
        ?.replace("frappe.exceptions.SecurityException: ", "")
        .replace("frappe.exceptions.RateLimitExceededError: ", "")
    || fallback
);

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email);

const getSafeRedirect = () => {
    const redirectTo = new URLSearchParams(window.location.search).get("redirect-to");

    if (!redirectTo) return undefined;

    try {
        const url = new URL(redirectTo, window.location.origin);
        return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : undefined;
    } catch {
        return undefined;
    }
};

const defaultCall = (args, url = "/") => window.frappe.call({
    type: "POST",
    url,
    args,
    freeze: true,
});

export const LoginPage = ({config: suppliedConfig, call = defaultCall, translate, onLoginSuccess}) => {
    const config = useMemo(() => ({...defaultConfig, ...getWindowConfig(), ...suppliedConfig}), [suppliedConfig]);
    const t = translate || ((label) => window.frappe?._?.(label) || label);

    const [view, setView] = useState("login");
    const [loginUsername, setLoginUsername] = useState(getRememberedUsername);
    const [loginPassword, setLoginPassword] = useState("");
    const [forgotUsername, setForgotUsername] = useState("");
    const [signupFullname, setSignupFullname] = useState("");
    const [signupEmail, setSignupEmail] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [shakingField, setShakingField] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const logo =  "/assets/trx_pos/images/logo-md.png";
    const appName =  t("TRX POS");
    const providerLogins = config.provider_logins || [];

    const viewContent = {
        login: {
            title: t("Welcome Back"),
            description: t("Login to your POS terminal and start your cashier shift."),
        },
        forgot: {
            title: t("Forgot Password?"),
            description: t("Enter your email address and we will send reset instructions."),
        },
        signup: {
            title: t("Create Your Account"),
            description: t("Enter your details to request a new POS account."),
        },
        "login-link": {
            title: t("Login with Email Link"),
            description: t("Enter your email address and we will send a secure login link."),
        },
    }[view];

    const resetMessages = () => {
        setErrorMessage(null);
        setSuccessMessage(null);
    };

    const setFieldError = (field, message) => {
        setErrors((current) => ({...current, [field]: message}));
        setShakingField(field);
        window.setTimeout(() => {
            setShakingField((current) => (current === field ? null : current));
        }, 1500);
    };

    const fieldClassName = (field) => [
        "form-control",
        errors[field] && "is-invalid",
        shakingField === field && "animate__animated animate__fast animate__shakeX",
    ].filter(Boolean).join(" ");

    const changeView = (nextView) => {
        if (loading) return;

        setView(nextView);
        setLoginUsername("");
        setLoginPassword("");
        setForgotUsername("");
        setSignupFullname("");
        setSignupEmail("");
        setShowPassword(false);
        setErrors({});
        setShakingField(null);
        resetMessages();
    };

    const handleLogin = async (event) => {
        event.preventDefault();

        if (!loginUsername.trim()) {
            setFieldError("login_username", t("Please enter your username"));
            return;
        }

        if (!loginPassword) {
            setErrors((current) => ({...current, login_username: undefined}));
            setFieldError("login_password", t("Please enter your password"));
            return;
        }

        setLoading(true);
        setErrors({});
        resetMessages();

        try {
            const response = await call({cmd: "login", usr: loginUsername.trim(), pwd: loginPassword}, "/login");

            setRememberedUsername(loginUsername.trim(), rememberMe);

            if (onLoginSuccess) {
                onLoginSuccess(response);
                return;
            }

            window.location.assign(getSafeRedirect() || "/pos");
        } catch (error) {
            setErrorMessage(getErrorMessage(
                error,
                error?.status === 401 ? t("Invalid Login. Try again.") : t("Unable to sign in. Please try again."),
            ));
            setLoading(false);
        } finally {
            // setLoading(false);
        }
    };

    const handleForgotPassword = async (event) => {
        event.preventDefault();

        if (!forgotUsername.trim()) {
            setFieldError("forgot_username", t("Please enter your email address"));
            return;
        }

        if (!isValidEmail(forgotUsername)) {
            setFieldError("forgot_username", t("Please enter a valid email address"));
            return;
        }

        setLoading(true);
        setErrors({});
        resetMessages();

        try {
            await call({cmd: "frappe.core.doctype.user.user.reset_password", user: forgotUsername.trim()});
            setForgotUsername("");
            setSuccessMessage(t("Password reset instructions have been sent."));
        } catch (error) {
            setErrorMessage(error?.status === 404
                ? t("User with email address does not exist.")
                : getErrorMessage(error, t("Unable to reset the password. Please try again.")));
        } finally {
            setLoading(false);
        }
    };

    const handleLoginLink = async (event) => {
        event.preventDefault();

        if (!loginUsername.trim()) {
            setFieldError("login_username", t("Please enter your email address"));
            return;
        }

        if (!isValidEmail(loginUsername)) {
            setFieldError("login_username", t("Please enter a valid email address"));
            return;
        }

        setLoading(true);
        setErrors({});
        resetMessages();

        try {
            await call({cmd: "frappe.www.login.send_login_link", email: loginUsername.trim()});
            setLoginUsername("");
            setSuccessMessage(t("Login link has been sent to your email."));
        } catch (error) {
            setErrorMessage(error?.status === 404
                ? t("Your email address is not registered.")
                : getErrorMessage(error, t("Unable to send the login link. Please try again.")));
        } finally {
            setLoading(false);
        }
    };

    const handleSignup = async (event) => {
        event.preventDefault();

        if (!signupFullname.trim()) {
            setFieldError("signup_fullname", t("Please enter your fullname"));
            return;
        }

        if (!signupEmail.trim()) {
            setErrors((current) => ({...current, signup_fullname: undefined}));
            setFieldError("signup_email", t("Please enter your email address"));
            return;
        }

        if (!isValidEmail(signupEmail)) {
            setFieldError("signup_email", t("Please enter a valid email address"));
            return;
        }

        setLoading(true);
        setErrors({});
        resetMessages();

        try {
            const response = await call({
                cmd: "frappe.core.doctype.user.user.sign_up",
                full_name: signupFullname.trim(),
                email: signupEmail.trim(),
                redirect_to: getSafeRedirect(),
            });
            const [status, message] = response?.message || [0, t("Unable to create your account.")];

            setSignupFullname("");
            setSignupEmail("");
            (Number(status) === 1 ? setSuccessMessage : setErrorMessage)(t(message));
        } catch (error) {
            setErrorMessage(getErrorMessage(error, t("Unable to create your account. Please try again.")));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="trx-login-page">
            <main className="page">
                <section className="left-panel" aria-label={t("POS overview")}>
                    <div className="brand">
                        <img src={logo} alt={appName} className="brand-logo"/>
                        <div>
                            <h1>{appName}</h1>
                            <p>{t("Modern point of sale system")}</p>
                        </div>
                    </div>

                    <div className="hero">
                        {/*<div className="badge">*/}
                        {/*    <span/>*/}
                        {/*    {t("POS Terminal Online")}*/}
                        {/*</div>*/}

                        <h2>
                            {t("Simple sales.")}<br/>
                            {t("Smart")} <strong>{t("control.")}</strong>
                        </h2>

                        <p className="hero-desc">
                            {t("Manage invoices, payments, customers, stock, shifts, and daily sales from one clean and fast POS interface.")}
                        </p>

                        <div className="dashboard-preview" aria-hidden="true">
                            <div className="preview-card activity-card">
                                <div className="card-label">{t("Today Activity")}</div>
                                <div className="card-title">{t("Active Orders")}</div>
                                <div className="card-tag">{t("Running smoothly")}</div>
                                <div className="chart">
                                    <span className="bar"/>
                                    <span className="bar"/>
                                    <span className="bar"/>
                                    <span className="bar"/>
                                    <span className="bar"/>
                                </div>
                            </div>

                            <div className="preview-card items-card">
                                <div className="card-label">{t("Popular Items")}</div>
                                <div className="item-row">
                                    <div className="item-icon">☕</div>
                                    <div>
                                        <strong>{t("Coffee Latte")}</strong>
                                        <small>{t("Fast moving item")}</small>
                                    </div>
                                </div>
                                <div className="item-row">
                                    <div className="item-icon">🍔</div>
                                    <div>
                                        <strong>{t("Beef Burger")}</strong>
                                        <small>{t("Customer favorite")}</small>
                                    </div>
                                </div>
                            </div>

                            <div className="preview-card status-card">
                                <div className="status-icon-large"><CheckIcon/></div>
                                <div className="card-label">{t("Last Payment")}</div>
                                <div className="card-title">{t("Approved")}</div>
                            </div>
                        </div>
                    </div>

                    <div className="left-footer">
                      ©  {new Date().getFullYear()} <strong>{t("TRX Solutions")}</strong>. {t("POS Management System.")}
                    </div>
                </section>

                <section className="right-panel">
                    <div className={`login-card${loading ? " is-loading" : ""}`} aria-busy={loading}>
                        <div className="login-inner">
                            <img src={logo} alt={appName} className="mobile-logo"/>

                            <div className="login-header">
                                <h3>{viewContent.title}</h3>
                                <p>{viewContent.description}</p>
                            </div>

                            {errorMessage && (
                                <div className="login-alert login-alert-error animate__animated animate__fast animate__shakeX" role="alert">
                                    <AlertIcon success={false}/>
                                    <span>{errorMessage}</span>
                                </div>
                            )}

                            {successMessage && (
                                <div className="login-alert login-alert-success animate__animated animate__fast animate__zoomIn" role="status">
                                    <AlertIcon success/>
                                    <span>{successMessage}</span>
                                </div>
                            )}

                            {view === "login" && (
                                <form onSubmit={handleLogin} className="login-form animate__animated animate__faster animate__zoomIn">
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="login_username">{t(config.login_label || "Username")}</label>
                                        <div className="input-box">
                                            <span className="input-icon"><UserIcon/></span>
                                            <input
                                                id="login_username"
                                                className={fieldClassName("login_username")}
                                                type="text"
                                                placeholder={config.login_name_placeholder || t("Enter username or email")}
                                                autoComplete="username"
                                                value={loginUsername}
                                                onChange={(event) => setLoginUsername(event.target.value)}
                                            />
                                        </div>
                                        {errors.login_username && <div className="invalid-feedback">{errors.login_username}</div>}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label" htmlFor="login_password">{t("Password")}</label>
                                        <div className="input-box">
                                            <span className="input-icon"><LockIcon/></span>
                                            <input
                                                id="login_password"
                                                className={fieldClassName("login_password")}
                                                type={showPassword ? "text" : "password"}
                                                placeholder={t("Enter password")}
                                                autoComplete="current-password"
                                                value={loginPassword}
                                                onChange={(event) => setLoginPassword(event.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle"
                                                onClick={() => setShowPassword((current) => !current)}
                                                aria-label={showPassword ? t("Hide password") : t("Show password")}
                                            >
                                                <EyeIcon hidden={showPassword}/>
                                            </button>
                                        </div>
                                        {errors.login_password && <div className="invalid-feedback">{errors.login_password}</div>}
                                    </div>

                                    <div className="row-options">
                                        {/*<label className="remember">*/}
                                        {/*    <input*/}
                                        {/*        type="checkbox"*/}
                                        {/*        checked={rememberMe}*/}
                                        {/*        onChange={(event) => setRememberMe(event.target.checked)}*/}
                                        {/*    />*/}
                                        {/*    <span>{t("Remember me")}</span>*/}
                                        {/*</label>*/}

                                        <button type="button" className="forgot" onClick={() => changeView("forgot")} disabled={loading}>
                                            {t("Forgot password?")}
                                        </button>
                                    </div>

                                    {!config.disable_user_pass_login && (
                                        <button type="submit" className="login-btn waves-effect waves-light" disabled={loading}>
                                            <span>{loading ? t("Logging in...") : t("Login to POS")}</span>
                                            {!loading && <ArrowIcon/>}
                                        </button>
                                    )}

                                    {(config.login_with_email_link || config.social_login || config.login_with_frappe_cloud_url) && (
                                        <div className="login-with">
                                            <div className="login-with-title"><span>{t("Or")}</span></div>
                                            {!!providerLogins.length && (
                                                <div className="provider-buttons">
                                                    {providerLogins.map((provider) => (
                                                        <button
                                                            key={provider.provider_name || provider.name}
                                                            type="button"
                                                            className="provider-btn"
                                                            disabled={loading}
                                                            tooltip={t(`Login with ${provider.provider_name}`)}
                                                            onClick={() => window.location.assign(provider.auth_url)}
                                                        >
                                                            {provider.icon
                                                                ? <span dangerouslySetInnerHTML={{__html: provider.icon}}/>
                                                                : <span>{provider.provider_name}</span>}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {config.login_with_frappe_cloud_url && config.login_with_frappe_cloud_url !== 'None' && (
                                                <button type="button" className="secondary-btn" disabled={loading} onClick={() => window.location.assign(config.login_with_frappe_cloud_url)}>
                                                    {t("Login with Frappe Cloud")}
                                                </button>
                                            )}
                                            {config.login_with_email_link && (
                                                <button type="button" className="secondary-btn" disabled={loading} onClick={() => changeView("login-link")}>
                                                    {t("Login with Email Link")}
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {!config.disable_signup && (
                                        <div className="switch-message">
                                            {t("Don't have an account?")}
                                            <button type="button" onClick={() => changeView("signup")} disabled={loading}>{t("Sign up")}</button>
                                        </div>
                                    )}
                                </form>
                            )}

                            {view === "forgot" && (
                                <form onSubmit={handleForgotPassword} className="login-form animate__animated animate__faster animate__zoomIn">
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="forgot_username">{t("Email Address")}</label>
                                        <div className="input-box">
                                            <span className="input-icon"><MailIcon/></span>
                                            <input
                                                id="forgot_username"
                                                className={fieldClassName("forgot_username")}
                                                type="email"
                                                placeholder={config.login_name_placeholder || t("name@example.com")}
                                                autoComplete="username"
                                                value={forgotUsername}
                                                onChange={(event) => setForgotUsername(event.target.value)}
                                            />
                                        </div>
                                        {errors.forgot_username && <div className="invalid-feedback">{errors.forgot_username}</div>}
                                    </div>

                                    <button type="submit" className="login-btn waves-effect waves-light" disabled={loading}>
                                        <span>{loading ? t("Sending...") : t("Reset Password")}</span>
                                    </button>
                                    <button type="button" className="secondary-btn full-width" onClick={() => changeView("login")} disabled={loading}>
                                        {t("Back to Login")}
                                    </button>
                                </form>
                            )}

                            {view === "login-link" && (
                                <form onSubmit={handleLoginLink} className="login-form animate__animated animate__faster animate__zoomIn">
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="login_link_email">{t("Email Address")}</label>
                                        <div className="input-box">
                                            <span className="input-icon"><MailIcon/></span>
                                            <input
                                                id="login_link_email"
                                                className={fieldClassName("login_username")}
                                                type="email"
                                                placeholder={config.login_name_placeholder || t("name@example.com")}
                                                autoComplete="username"
                                                value={loginUsername}
                                                onChange={(event) => setLoginUsername(event.target.value)}
                                            />
                                        </div>
                                        {errors.login_username && <div className="invalid-feedback">{errors.login_username}</div>}
                                    </div>

                                    <button type="submit" className="login-btn waves-effect waves-light" disabled={loading}>
                                        <span>{loading ? t("Sending...") : t("Send login link")}</span>
                                    </button>
                                    <button type="button" className="secondary-btn full-width" onClick={() => changeView("login")} disabled={loading}>
                                        {t("Back to Login")}
                                    </button>
                                </form>
                            )}

                            {view === "signup" && !config.disable_signup && (
                                <form onSubmit={handleSignup} className="login-form animate__animated animate__faster animate__zoomIn">
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="signup_fullname">{t("Full Name")}</label>
                                        <div className="input-box">
                                            <span className="input-icon"><UserIcon/></span>
                                            <input
                                                id="signup_fullname"
                                                className={fieldClassName("signup_fullname")}
                                                type="text"
                                                placeholder={t("Full name")}
                                                autoComplete="name"
                                                value={signupFullname}
                                                onChange={(event) => setSignupFullname(event.target.value)}
                                            />
                                        </div>
                                        {errors.signup_fullname && <div className="invalid-feedback">{errors.signup_fullname}</div>}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label" htmlFor="signup_email">{t("Email")}</label>
                                        <div className="input-box">
                                            <span className="input-icon"><MailIcon/></span>
                                            <input
                                                id="signup_email"
                                                className={fieldClassName("signup_email")}
                                                type="email"
                                                placeholder={config.login_name_placeholder || t("name@example.com")}
                                                autoComplete="username"
                                                value={signupEmail}
                                                onChange={(event) => setSignupEmail(event.target.value)}
                                            />
                                        </div>
                                        {errors.signup_email && <div className="invalid-feedback">{errors.signup_email}</div>}
                                    </div>

                                    <button type="submit" className="login-btn waves-effect waves-light" disabled={loading}>
                                        <span>{loading ? t("Creating...") : t("Sign up")}</span>
                                    </button>
                                    <div className="switch-message">
                                        {t("Have an account?")}
                                        <button type="button" onClick={() => changeView("login")} disabled={loading}>{t("Login")}</button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};
